import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { 
  CalendarIcon,
  MapPinIcon,
  StarIcon
} from "lucide-react";
import { getProductDetailUrl, getSupplierProfileUrl } from "@/lib/navigation-helpers";

export interface SearchResultItemProps {
  id: string;
  name: string;
  description: string;
  image: string;
  price: number;
  currency: string;
  condition: "New" | "Used" | "Refurbished";
  category?: string;
  year?: number;
  manufacturer?: string;
  location?: string;
  rating?: number;
  reviewCount?: number;
  supplierName?: string;
  supplierSlug?: string;
  featured?: boolean;
}

/**
 * A reusable component for displaying search results with supplier information
 */
export default function SearchResultItem({
  id,
  name,
  description,
  image,
  price,
  currency,
  condition,
  category,
  year,
  manufacturer,
  location,
  rating,
  reviewCount,
  supplierName,
  supplierSlug,
  featured
}: SearchResultItemProps) {
  const formattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(price);

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case "New":
        return "bg-green-100 text-green-800 dark:bg-green-400/20 dark:text-green-400";
      case "Used":
        return "bg-blue-100 text-blue-800 dark:bg-blue-400/20 dark:text-blue-400";
      case "Refurbished":
        return "bg-purple-100 text-purple-800 dark:bg-purple-400/20 dark:text-purple-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-400/20 dark:text-gray-400";
    }
  };

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <div className="flex flex-col md:flex-row">
        <div className="w-full md:w-1/4 h-48 md:h-auto relative">
          <Link to={getProductDetailUrl(id)}>
            <img
              src={image}
              alt={name}
              className="w-full h-full object-cover"
            />
          </Link>
          <div className="absolute top-2 left-2">
            <Badge className={getConditionColor(condition)}>{condition}</Badge>
            {featured && (
              <Badge className="ml-1 bg-primary text-white">Featured</Badge>
            )}
          </div>
        </div>
        <div className="flex-1 p-4">
          <div className="flex justify-between items-start">
            <div>
              <Link 
                to={getProductDetailUrl(id)} 
                className="text-lg font-semibold hover:text-primary hover:underline"
              >
                {name}
              </Link>
              <div className="flex items-center gap-x-3 text-sm text-muted-foreground mt-1">
                {manufacturer && <span>{manufacturer}</span>}
                {year && (
                  <span className="flex items-center gap-x-1">
                    <CalendarIcon className="h-3 w-3" /> {year}
                  </span>
                )}
                {location && (
                  <span className="flex items-center gap-x-1">
                    <MapPinIcon className="h-3 w-3" /> {location}
                  </span>
                )}
              </div>
            </div>
            <div className="text-xl font-bold">{formattedPrice}</div>
          </div>
          
          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
            {description}
          </p>
          
          {/* Rating display */}
          {typeof rating === 'number' && (
            <div className="flex items-center gap-1 mt-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <StarIcon
                    key={i}
                    className={`h-4 w-4 ${
                      i < Math.floor(rating)
                        ? "text-yellow-500 fill-yellow-500"
                        : "text-gray-300"
                    }`}
                  />
                ))}
              </div>
              {reviewCount !== undefined && (
                <span className="text-xs text-muted-foreground">
                  ({reviewCount})
                </span>
              )}
            </div>
          )}
          
          {/* Supplier Information - exactly as requested */}
          {supplierName && supplierSlug && (
            <div className="text-sm text-muted-foreground mt-1">
              <span>Supplier: </span>
              <Link 
                to={getSupplierProfileUrl(supplierSlug)} 
                className="hover:underline text-primary"
              >
                {supplierName}
              </Link>
            </div>
          )}
          
          {/* Category display if needed */}
          {category && (
            <div className="mt-2">
              <Badge variant="outline">{category}</Badge>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
